import { surveyQuestionNumber } from "../../../../../shared/survey-questions";
import { Input, Label, Textarea } from "@/components/ui";
import type { MatterSurveyFillQuestion } from "@/lib/data/requests/matters/matter-survey";
import { CircleAlert, CircleCheck, LoaderCircle } from "lucide-react";
import type {
  SurveyDraftAnswer,
  SurveyDraftSaveStatus,
} from "./useStaffSurveyDraft";

type Answer = SurveyDraftAnswer;

function hasAnswer(value: Answer | undefined) {
  return Array.isArray(value) ? value.length > 0 : Boolean(value?.trim());
}

function DraftStatus({ status }: { status: SurveyDraftSaveStatus }) {
  if (status === "idle") return null;
  return (
    <span
      role="status"
      aria-label={
        status === "saving"
          ? "Saving answer"
          : status === "saved"
            ? "Answer saved"
            : "Answer could not be saved"
      }
      className="inline-flex size-4 shrink-0 items-center justify-center"
    >
      {status === "saving" ? (
        <LoaderCircle
          className="text-muted-foreground size-4 animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
      ) : status === "saved" ? (
        <CircleCheck className="size-4 text-green-600" aria-hidden="true" />
      ) : (
        <CircleAlert className="text-destructive size-4" aria-hidden="true" />
      )}
    </span>
  );
}

export function SurveyAnswerField({
  question,
  number,
  depth = 0,
  value,
  onChange,
  disabled,
  error,
  saveStatus,
}: {
  question: MatterSurveyFillQuestion;
  number: string;
  depth?: number;
  value: Answer;
  onChange: (value: Answer) => void;
  disabled: boolean;
  error: boolean;
  saveStatus: SurveyDraftSaveStatus;
}) {
  const fieldId = `staff-survey-${question.id}`;
  const fieldLabel = `${surveyQuestionNumber(number)} ${question.prompt}`;
  const textValue = typeof value === "string" ? value : "";

  return (
    <div
      className="space-y-2"
      style={{ marginLeft: `${Math.min(depth, 3) * 16}px` }}
    >
      <div className="flex items-center gap-2">
        <Label htmlFor={fieldId} className="block leading-5">
          {fieldLabel}
          {question.required ? (
            <span className="text-destructive"> *</span>
          ) : null}
        </Label>
        <DraftStatus status={saveStatus} />
      </div>
      {question.type === "long_answer" ? (
        <Textarea
          id={fieldId}
          rows={3}
          value={textValue}
          disabled={disabled}
          aria-invalid={error}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      ) : question.type === "multiple_choice" ||
        question.type === "checkboxes" ? (
        <div role="group" aria-label={fieldLabel} className="space-y-2">
          {question.type === "multiple_choice" && !question.required ? (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={question.id}
                checked={!hasAnswer(value)}
                disabled={disabled}
                onChange={() => {
                  onChange(null);
                }}
              />
              <span>No answer</span>
            </label>
          ) : null}
          {question.options.map((option) => {
            const checked = Array.isArray(value)
              ? value.includes(option.id)
              : value === option.id;
            return (
              <label key={option.id} className="flex items-start gap-2 text-sm">
                <input
                  type={question.type === "checkboxes" ? "checkbox" : "radio"}
                  name={question.id}
                  value={option.id}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => {
                    if (question.type === "multiple_choice") {
                      onChange(option.id);
                    } else {
                      const selected = Array.isArray(value) ? value : [];
                      onChange(
                        checked
                          ? selected.filter((id) => id !== option.id)
                          : [...selected, option.id],
                      );
                    }
                  }}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      ) : (
        <Input
          id={fieldId}
          type={
            question.type === "datetime"
              ? "datetime-local"
              : question.type === "short_answer"
                ? "text"
                : question.type
          }
          value={textValue}
          disabled={disabled}
          aria-invalid={error}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      )}
      {error ? (
        <p className="text-destructive text-xs">
          This question needs an answer.
        </p>
      ) : null}
    </div>
  );
}
